import {
  useContext,
  useEffect,
  useState,
} from "react";
import { Button} from "react-bootstrap";
import { BsExclamationCircle, BsSave } from "react-icons/bs";
import { FiArchive } from "react-icons/fi";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import MultiTabComponent, {
  ITab,
} from "@/manager/components/multi-tab-component/multi-tab-component";
import { ITableRowData } from "@/manager/components/data-table/data-table";
import NormalTable from "@/shared/components/normal-table/normal-table";
import SimpleModal from "@/shared/components/simple-modal/simple-modal";
import AuthContext from "@/shared/context/auth-context";
import useHttp from "@/shared/hooks/use-http";
import {
  IDetailPageData,
  failedToast,
  getAllData,
  postSingle,
} from "@/shared/utils/api";
import {
  fieldtypeOptions,
  defaultSettingsKeys,
} from "@/shared/utils/static-values";
import {
  createUUID,
  validateLoadingStatusNew,
} from "@/shared/utils/utils";
import Tooltip from "@/shared/components/tooltip/tooltip";
import "./datamodel-page.scss";
import Page from "@/manager/components/page/page";
import { getAnalyticsUrl, getManagerUrl } from "@/config";
import ReferencingComponentTab, { IDatamodelRelation } from "./referencing-component-tab";
import SettingsTab from "./settings-tab";
import DatamodelTable from "@/platform/components/datamodel/datamodel-table";
import Spinner from "@/shared/components/spinner/spinner";
import './datamodel-page.scss';
import { GrRevert } from "react-icons/gr";

interface IDatamodelPageProps {
  datamodelType: "event" | "entity" | "alertcase";
}

export interface IDatamodelSettings {
  csv_separator: string;
  datetime_format: string;
  alertcase: string;
  alertcase_id: string;
  alertcase_field_id: string;
  alertcase_field: string;
  alertcase_label_field_id: string;
  alertcase_label_field: string;
  alertcase_list_id: string;
  alertcase_list: string;
  label_list: string;
  label_list_id: string;
  enable_scoring: string;
  primary_datetime_column_id: string;
  primary_datetime_column: string;
  process_type_column_id: string;
  process_type_column: string;
}

const DatamodelPage = ({ datamodelType }: IDatamodelPageProps) => {
  const managerEnabled = getManagerUrl() !== "";
  const analyticsEnabled = getAnalyticsUrl() !== "";
  const params = useParams();
  const navigate = useNavigate();
  const isNew = !params.id

  const [data, setData] = useState<IDetailPageData>({
    id: params.id || createUUID(),
    name: "",
    archived: false,
    rowData: [],
  });
  const authCtx = useContext(AuthContext);
  const [settingsData, setSettingsData] =
    useState<IDatamodelSettings>(defaultSettingsKeys);
  const [showSaveDatamodelFieldsModal, setShowSaveDatamodelFieldsModal] = useState(false);
  const [referencingComponentsData, setReferencingComponentsData] = useState<IDatamodelRelation[]>([]);
  
  const {
    sendRequest: getDatamodelFields,
    status: getDatamodelFieldsStatus,
    data: getDatamodelFieldsData,
    error: getDatamodelFieldsError,
  } = useHttp(getAllData, !isNew, authCtx.token, datamodelType);

  const {
    sendRequest: postDatamodelFields,
    status: postDatamodelFieldsStatus,
    data: postDatamodelFieldsData,
    error: postDatamodelFieldsError,
  } = useHttp(postSingle, false, authCtx.token, datamodelType);

  const {
    sendRequest: postConsolidationSettings,
    status: postConsolidationSettingsStatus,
    data: postConsolidationSettingsData,
    error: postConsolidationSettingsError,
  } = useHttp(postSingle, false, authCtx.token, "eventConsolidationInfo", "manager");
  
  const {
    sendRequest: getEventConsolidation,
    status: getEventConsolidationStatus,
    data: getEventConsolidationData,
    error: getEventConsolidationError,
  } = useHttp(getAllData, false, authCtx.token, "eventConsolidationInfo", "manager");

  const { sendRequest: getReferencingComponents, status: getReferencingComponentsStatus, data: getReferencingComponentsData, error: getReferencingComponentsError } = useHttp(
    getAllData, 
    false, 
    authCtx.token, 
    "referencingComponents", 
    "platform"
  );

  const { sendRequest: postReferencingComponents, status: postReferencingComponentsStatus, data: postReferencingComponentsData, error: postReferencingComponentsError } = useHttp(
    postSingle, 
    false, 
    authCtx.token, 
    "referencingComponents", 
    "platform"
  );

  useEffect(() => {
    if (postReferencingComponentsStatus === "completed" && !postReferencingComponentsError && postReferencingComponentsData) {
      setReferencingComponentsData(postReferencingComponentsData || []);
    }
  }, [postReferencingComponentsStatus, postReferencingComponentsData, postReferencingComponentsError]);

  useEffect(() => {
    if (!params.id) {
      return;
    }
    if (managerEnabled) {
      getEventConsolidation(`event_consolidation/${params.id}`);
    }

    getDatamodelFields(
      `datamodel/${datamodelType}/${params.id}`,
      "?order_by=input_field_name asc"
    );

    getReferencingComponents(`referencing_components/${params.id}`);
  }, [params.id]);

  useEffect(() => {
    if (getEventConsolidationStatus === "completed" && !getEventConsolidationError && getEventConsolidationData) {
      setSettingsData({
        ...settingsData,
        ...getEventConsolidationData
      });
    }
  }, [getEventConsolidationStatus, getEventConsolidationData, getEventConsolidationError]);

  useEffect(() => {
    if (postConsolidationSettingsStatus === "completed" && !postConsolidationSettingsError && postConsolidationSettingsData) {
      setSettingsData({
        ...settingsData,
        ...postConsolidationSettingsData
      });
    }
  }, [postConsolidationSettingsStatus, postConsolidationSettingsData, postConsolidationSettingsError]);

  useEffect(() => {
    if (getReferencingComponentsStatus === "completed" && !getReferencingComponentsError && getReferencingComponentsData) {
      setReferencingComponentsData(getReferencingComponentsData || []);
    }
  }, [getReferencingComponentsStatus, getReferencingComponentsData, getReferencingComponentsError]);

  useEffect(() => {
    if (getDatamodelFieldsStatus === "completed" && !getDatamodelFieldsError && getDatamodelFieldsData) {
      setData({...data, 
        rowData: getDatamodelFieldsData.rowData || [],
        id: getDatamodelFieldsData.id,
        name: getDatamodelFieldsData.name,
        archived: getDatamodelFieldsData.archived
      });

      setSettingsData(({
        ...settingsData,
        csv_separator: getDatamodelFieldsData.csv_separator,
        datetime_format: getDatamodelFieldsData.datetime_format,
        alertcase: getDatamodelFieldsData.alertcase,
        alertcase_id: getDatamodelFieldsData.alertcase_id,
        alertcase_field: getDatamodelFieldsData.alertcase_field,
        alertcase_field_id: getDatamodelFieldsData.alertcase_field_id,
        alertcase_label_field: getDatamodelFieldsData.alertcase_label_field,
        alertcase_label_field_id: getDatamodelFieldsData.alertcase_label_field_id,
        alertcase_list: getDatamodelFieldsData.alertcase_list,
        alertcase_list_id: getDatamodelFieldsData.alertcase_list_id,
        label_list: getDatamodelFieldsData.label_list,
        label_list_id: getDatamodelFieldsData.label_list_id,
        enable_scoring: getDatamodelFieldsData.enable_scoring
      }));
    }
  }, [getDatamodelFieldsStatus, getDatamodelFieldsData, getDatamodelFieldsError]);

  useEffect(() => {
    if (postDatamodelFieldsStatus === "completed" && !postDatamodelFieldsError && postDatamodelFieldsData) {
      const returnUrl =
        datamodelType === "entity"
          ? "/entities"
          : datamodelType === "alertcase"
          ? "/alertcasetypes"
          : "/events";
      navigate(returnUrl);
    }
  }, [postDatamodelFieldsStatus, postDatamodelFieldsData, postDatamodelFieldsError]);

  const renderPathButtons = () => {
    const typeMap = {
      entity: { returnUrl: "/entities", typeLabel: "Entity", typesLabel: "Entities" },
      alertcase: { returnUrl: "/alertcasetypes", typeLabel: "Alertcase", typesLabel: "Alertcases" },
      event: { returnUrl: "/events", typeLabel: "Event", typesLabel: "Events" },
    };
    const { returnUrl, typeLabel, typesLabel } = typeMap[datamodelType];

    return (
      <span>
        <span onClick={() => navigate(returnUrl)} className="optionPath">
          {typesLabel} types
        </span>
        <span className="optionPathDelimiter">-</span>
        <span className="selectedPathBtn">
          {isNew
            ? `New ${typeLabel.toLowerCase()} type`
            : `${typeLabel} type`}
        </span>
      </span>
    );
  };

  const renderSaveDatamodelModalBody = () => {
    if (data?.rowData?.length === undefined) {
      return;
    }

    if (isNew) {
      return <p>You are about to create a new datamodel and persist your changes to the database, are you sure?</p>;
    }

    const originalData: ITableRowData[] = getDatamodelFieldsData?.rowData || [];
    const dataCopy = data!.rowData.map(d => ({...d}));

    // Find changed and added rows
    const changedRows = dataCopy.filter(row => {
      const origRow = originalData.find(oRow => oRow.id === row.id);
      return !origRow || (origRow && (
      origRow.input_field_name !== row.input_field_name ||
      origRow.field_datatype !== row.field_datatype ||
      origRow.text_type !== row.text_type ||
      origRow.primary_key !== row.primary_key
      ));
    }).map(row => {
      const originalDataField = originalData.find(oRow => oRow.id === row.id);
      let dataLoss = false;
      let changeType = "";

      if (!originalDataField) {
        changeType = "Added";
      } else {
        changeType = "Edited";

        if (row.input_field_name !== originalDataField.input_field_name) {
          row.input_field_name = `${originalDataField.input_field_name} >> ${row.input_field_name}`;
        }

        if (originalDataField.field_datatype !== row.field_datatype) {
          dataLoss = true;
          row.field_datatype = `${originalDataField.field_datatype} >> ${row.field_datatype}`;
        }
        if (originalDataField.text_type !== row.text_type) {
          dataLoss = true;
        }
      }

      return {
      ...row,
      changeType,
      dataLoss: dataLoss ? <BsExclamationCircle /> : "",
      revert: <GrRevert onClick={() => {
        if (changeType === "Added") {
          // Remove the added row
          setData(prevData => ({
            ...prevData,
            rowData: prevData.rowData.filter(r => r.id !== row.id),
          }));
          return;
        } else {
          setData(prevData => {
            const updatedRowData = prevData.rowData.map(r => {
              if (r.id === row.id) {
                return originalData.find(oRow => oRow.id === row.id)!;
              }
              return r;
            });
            return {
              ...prevData,
              rowData: updatedRowData,
            };
          });
        }
      }}/>,
      };
    });

    const deletedRows = originalData
      .filter(origRow => !dataCopy.some(row => row.id === origRow.id))
      .map(origRow => ({
        ...origRow,
        changeType: "Deleted",
        dataLoss: <BsExclamationCircle />,
        revert: <GrRevert onClick={() => {
          setData(prevData => ({
            ...prevData,
            rowData: [...prevData.rowData, origRow],
          }));
        }}/>
      }));

    const allChangedRows = [...changedRows, ...deletedRows].sort((a, b) => {
      // Sort by dataLoss first (those with dataLoss come first), then by input_field_name
      return a.dataLoss ? -1 : b.dataLoss ? 1 : 0;
    });

    return (
      <div>
        <p>You are about to persist your changes to the database, are you sure?</p>
        <div>
          {allChangedRows.some(row => !!row.dataLoss) && <div>
            <b>Warning:</b>
            The following changes will result in a data loss. &nbsp;
            <Tooltip
              content={
                "Deletion of a column or changing a FieldType will result in complete data loss for those columns."
              }
              direction="top"
              delay={350}
            >
              <BsExclamationCircle />
            </Tooltip>
          </div>
          }
        </div>
        {allChangedRows.length > 0 && <div className="popup-showMoreResults">
          <NormalTable
            fetchedData={{
              rows: allChangedRows,
            }}
            allStatus="completed"
            columns={[
              {
                key: "dataLoss",
                displayName: "Data loss",
                dataType: {
                  type: "text",
                },
                nonSortable: true,
              },
              {
                key: "changeType",
                displayName: "Change type",
                dataType: {
                  type: "text",
                },
                nonSortable: true,
              },
              {
                key: "input_field_name",
                displayName: "Input field name",
                dataType: {
                  type: "text",
                },
                controlled: true,
                nonSortable: true,
              },
              {
                key: "field_datatype",
                displayName: "Fieldtype",
                dataType: {
                  type: "enumeration",
                  values: fieldtypeOptions,
                },
                nonSortable: true,
              },
              {
                key: "revert",
                displayName: "",
                dataType: {
                  type: "text"
                },
                nonSortable: true,
              }
            ]}
            hideSearchInput={true}
            hideCheckBoxes={true}
            hideButtons={["delete", "add"]}
            isRowClickable={false}
          />
        </div>
        }
      </div>
    );
  };

  const handleSaveDatamodel = () => {
    if (!data) {
      return;
    }

    const errors: string[] = [];
    if (!data.name) {
      errors.push("Datamodel must have a name.");
    }
    if (!data.rowData || data.rowData.length === 0) {
      errors.push("Datamodel must have at least one field.");
    }

    ['csv_separator', 'datetime_format'].forEach((key) => {
      const k = key as keyof IDatamodelSettings;
      if (settingsData[k] === undefined || settingsData[k] === '') {
        errors.push(`Datamodel setting ${key.replace(/_/g, " ")} must have a value.`);
      }
    });

    if (settingsData.alertcase_id) {
      if (analyticsEnabled) {
        ['alertcase_field_id', 'alertcase_label_field_id', 'enable_scoring'].forEach((key) => {
          const k = key as keyof IDatamodelSettings;
          if (settingsData[k] === undefined || settingsData[k] === '') {
            errors.push(`Datamodel setting ${key.replace(/_/g, " ")} must have a value when alertcase is set.`);
          }
        });
      }
      if (managerEnabled) {
        ['primary_datetime_column_id', 'process_type_column_id'].forEach((key) => {
          const k = key as keyof IDatamodelSettings;
          if (settingsData[k] === undefined || settingsData[k] === '') {
            errors.push(`Datamodel setting ${key.replace(/_/g, " ")} must have a value when alertcase is set.`);
          }
        });
      }
    }

    const fieldNames = data.rowData?.map(row => row.input_field_name) || [];
    const hasDuplicates = new Set(fieldNames).size !== fieldNames.length;
    if (hasDuplicates) {
      errors.push("Field names must be unique.");
    }
    // Example: check for empty field names
    if (fieldNames.some(name => !name || name.trim() === "")) {
      errors.push("All fields must have a name.");
    }

    if (errors.length > 0) {
      failedToast(errors.join("\n"));
      return;
    }

    postDatamodelFields(
      `datamodel/${datamodelType}/${data.id}?order_by=input_field_name asc`,
      {
        id: data.id,
        name: data.name,
        rowData: data?.rowData || [],
        ...settingsData,
      }
    );

    const referencingComponents = referencingComponentsData.map(referencingComponent => ({
      ...referencingComponent,
      source_datamodel_id: data.id,
      source_type: datamodelType
    }))
    
    postReferencingComponents(
      `referencing_components/${data.id}`,
      referencingComponents,
      undefined,
      "none"
    )

    if (managerEnabled && datamodelType === "event" && settingsData.alertcase_id) {
      postConsolidationSettings(`event_consolidation/${data.id}`, settingsData, undefined, "none");
    }

    setShowSaveDatamodelFieldsModal(false);
  };

  const handleUnarchive = () => {
    if (data?.archived) {
      postDatamodelFields(
      `datamodel/${datamodelType}/${data.id}?order_by=input_field_name asc`,
      {
        id: data.id,
        archived: false,
        name: data.name,
        rowData: data?.rowData || [],
        ...settingsData,
      }
    );
    }
  }

  const tabs: ITab[] = [
    {
      name: "Fields",
      key: "fields",
      content: <DatamodelTable
        datamodelType={datamodelType}
        data={data?.rowData || []}
        readonly={!!data?.archived}
        onDataUpdated={(updatedData) => setData(prevData => ({ ...prevData, rowData: updatedData }))}
        onRevert={() => {
          getDatamodelFields(
            `datamodel/${datamodelType}/${params.id}`,
            "?order_by=input_field_name asc"
          );
        }}
      />
    },
    {
      name: "Settings",
      key: "settings",
      content: <SettingsTab datamodelType={datamodelType} datamodelSettings={settingsData} datamodelFields={data?.rowData || []} handleSettingsChange={setSettingsData} />,
    },
    {
      name: "Referencing components",
      key: "referencingComponents",
      content: <ReferencingComponentTab
        datamodelType={datamodelType}
        datamodelFields={data.rowData.map(row => ({ id: row.id, field_name: row.input_field_name, field_datatype: row.field_datatype }))}
        referencingComponents={referencingComponentsData || []}
        onReferencingComponentsChange={(components) => {setReferencingComponentsData(components)}
        }
      />
    }
  ];

  return (
    <Page title={{
      text: data?.name || "",
      onTitleEdited: (title: string) => {
        setData({...data, name: title });
      },
      placeholder: "Enter datamodel name",
    }}>
      <div className="datamodelPage">
        <div className="datamodelPage-header">
          {renderPathButtons && renderPathButtons()}
          <div className="datamodelPage-actionButtons">
            {data?.archived === true && 
              <Tooltip content={"Unarchive"} direction="top" delay={350}>
                <Button className="datamodelPage-unarchiveButton" onClick={() => handleUnarchive()}>
                  <FiArchive/>
                </Button>
              </Tooltip>

            }
            <Tooltip content={"Save all changes"} direction="top" delay={350}>
              <Button disabled={!data || data.rowData.length === 0 || !data.name || data?.archived === true} className="datamodelPage-saveButton" onClick={() => setShowSaveDatamodelFieldsModal(true)}>
                <BsSave/>
              </Button>
            </Tooltip>
          </div>
        </div>
        <div>
          {validateLoadingStatusNew([
            getDatamodelFieldsStatus,
            postDatamodelFieldsStatus,
            postConsolidationSettingsStatus
          ]) && <Spinner />}

          {/* Save Datamodel Fields Confirmation Modal */}
          <SimpleModal
            modalBody={renderSaveDatamodelModalBody()}
            buttonText={"Confirm"}
            mode={"Saving"}
            wrapperClass={"confirmationModal"}
            handleModalClosed={() => {
              setShowSaveDatamodelFieldsModal(false);
            }}
            handleSubmit={handleSaveDatamodel}
            show={showSaveDatamodelFieldsModal}
            showCancelButton={true}
          />
          <MultiTabComponent
            tabs={tabs}
            displayNextButton={false}
            enableHashRouting={true}
          />
        </div>
      </div>
    </Page>
  );
};

export default DatamodelPage;
